import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { apiClient } from '@kss/api';
import { postStockMovements, resolveDefaultWarehouseId } from '../../utils/inventory';
import { postSalesPayment, paymentStatusFor } from '../../utils/payments';
import { compactDescEnvelope } from '../../utils/descEnvelope';
import { postLinesSequential, createHeaderAndGetId } from '../../utils/postLines';
import { lookupCoupon, applyCoupon, type CouponRow } from '../../utils/coupons';
import { lookupGiftCard, applyGiftCard, type GiftCardRow } from '../../utils/giftcards';
import { ArrowLeft, ChevronDown, Download, MessageCircle, Minus, Plus, Printer, Save, Send, Share2, Trash2, X } from 'lucide-react';
import { EntityPicker } from './EntityPicker';
import { printInvoiceByFormat, type InvoicePrintInput, type HsnSummaryRow } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeader, getPrintBrandingInlined, getTenantHeaderInlined, printQrUrl } from '../../print/branding';

/**
 * GST-compliant Sales Invoice composer.
 *
 * Replaces the registry-driven GenericCrudForm for SalesInvoice with a
 * proper multi-line editor: customer typeahead, invoice header, dynamic
 * line-item table (product picker, qty, rate, discount %, GST class)
 * and live-calculated totals (subtotal, discount, taxable, CGST/SGST or
 * IGST split, round-off, grand total).
 *
 * The CGST/SGST vs IGST decision is made client-side from
 * `customer.state` vs the supplier-of-record `placeOfSupply` (defaults
 * to Maharashtra "27" — overridable by tenant settings later).
 */

interface Customer extends Record<string, unknown> {
  customerId?: string;
  id?: number;
  code?: string;
  name?: string;
  gstin?: string;
  state?: string;
}

interface Product extends Record<string, unknown> {
  productId?: string;
  id?: number;
  code?: string;
  name?: string;
  hsnCode?: string;
  sellingPrice?: number;
  taxClassId?: string;
}

interface TaxClass extends Record<string, unknown> {
  taxClassId?: string;
  id?: number;
  code?: string;
  name?: string;
  rate?: number;
}

interface Line {
  key: string;
  productId: string | number | null;
  productLabel: string;
  hsnCode: string;
  qty: number;
  unit: string;        // §35: from product.unitOfMeasure (PCS / KG / LTR / …)
  rate: number;
  discountPct: number;
  taxRate: number;
  /** Set when a ProductVariant is chosen for this line (additive: the
   *  product picker is unchanged; a small Variant dropdown appears next to
   *  it when the picked product has variants). */
  productVariantId?: number | string | null;
}

const STATE_CODE_MAP: Record<string, string> = {
  // 2-digit GSTIN state code; covers the common ones — extend later.
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
  '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
  '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
  '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '27': 'Maharashtra', '29': 'Karnataka', '32': 'Kerala', '33': 'Tamil Nadu',
  '36': 'Telangana', '37': 'Andhra Pradesh',
};

const DEFAULT_PLACE_OF_SUPPLY = '27'; // Maharashtra; tenant-config-driven later.

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[60px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

export default function SalesInvoiceComposer() {
  const { id: rawId } = useParams<{ id?: string }>();
  const isEdit = !!rawId && rawId !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  // Header
  // Seed a human-visible invoice number so the field isn't blank on a new
  // invoice. The backend still owns the authoritative sequence; this is
  // just a friendly default the user can overwrite. (Edit mode overwrites
  // it from the loaded record below.)
  const [invoiceNumber, setInvoiceNumber] = useState(
    () => `SI-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${String(new Date().getTime() % 10000).padStart(4, '0')}`,
  );
  // Tracks whether the user has hand-edited the number, so the sequential
  // auto-fill below never clobbers a manual override.
  const numberTouched = useRef(false);

  // For a NEW invoice, replace the random provisional suffix with a proper
  // SEQUENTIAL number (SI-YYMMDD-0001, -0002, …) so consecutive invoices
  // increment line-to-line instead of jumping around. Reads the highest
  // suffix already used for today and adds one. (Edit mode loads the saved
  // number instead — see the hydrate effect below.)
  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    void (async () => {
      try {
        const ymd = new Date().toISOString().slice(2, 10).replace(/-/g, '');
        const prefix = `SI-${ymd}-`;
        const r = await apiClient.get('/sales/SalesInvoice');
        const rows = (Array.isArray(r.data) ? r.data : (r.data?.items ?? r.data?.data ?? [])) as Array<Record<string, unknown>>;
        let max = 0;
        for (const row of rows) {
          const num = String(row.invoiceNumber ?? '');
          if (!num.startsWith(prefix)) continue;
          const n = parseInt(num.slice(prefix.length), 10);
          if (Number.isFinite(n) && n > max) max = n;
        }
        if (!cancelled && !numberTouched.current) {
          setInvoiceNumber(`${prefix}${String(max + 1).padStart(4, '0')}`);
        }
      } catch { /* keep the provisional number */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');
  // Amount collected at invoice time (drives paidAmount/balance + a SalesPayment).
  const [amountReceived, setAmountReceived] = useState(0);
  const [payMethod, setPayMethod] = useState('Cash');
  // Captured on edit-hydrate: the row's real `code` (often empty for POS/SI
  // invoices) + its saved header grandTotal (shown when the invoice has no
  // itemised line rows, so it doesn't read ₹0).
  const [savedCode, setSavedCode] = useState('');
  const [savedHeaderTotal, setSavedHeaderTotal] = useState(0);
  // Company header for the print — loaded from the live Tenant, never hardcoded.
  const [tenantHdr, setTenantHdr] = useState<InvoicePrintInput['tenant']>({ name: 'My Company' });
  useEffect(() => {
    // No backend tenant endpoint (`/tenants/Tenant/me` → 404, gap G86) — read
    // the print header from the admin-set branding (Settings → Print Formats).
    setTenantHdr(getTenantHeader());
  }, []);
  const [terms, setTerms] = useState('Goods once sold cannot be returned. Subject to local jurisdiction.');

  // Customer
  const [customer, setCustomer] = useState<Customer | null>(null);
  // Bumped after an inline quick-add so the picker re-fetches its list.
  const [customerReloadKey, setCustomerReloadKey] = useState(0);

  // Lines
  const [lines, setLines] = useState<Line[]>([
    { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 },
  ]);

  // Tax classes (used to map productId → taxRate when picking a product)
  const [taxClasses, setTaxClasses] = useState<TaxClass[]>([]);
  useEffect(() => {
    apiClient
      .get('/master-data/TaxClass')
      .then((r) => {
        const d = r.data;
        const list = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
        setTaxClasses(Array.isArray(list) ? list : []);
      })
      .catch(() => setTaxClasses([]));
  }, []);

  // #1 Variants (per-line dropdown) + #2 Bundles ("Add bundle" expands
  // into component lines). Loaded once; the product picker is unchanged.
  const [variants, setVariants] = useState<Array<Record<string, unknown>>>([]);
  const [bundleItems, setBundleItems] = useState<Array<Record<string, unknown>>>([]);
  const [bundles, setBundles] = useState<Array<Record<string, unknown>>>([]);
  const [productsById, setProductsById] = useState<Record<string, Record<string, unknown>>>({});
  useEffect(() => {
    const pull = (p: string) =>
      apiClient.get(p).then((r) => {
        const d = r.data;
        const l = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
        return Array.isArray(l) ? (l as Array<Record<string, unknown>>) : [];
      }).catch(() => [] as Array<Record<string, unknown>>);
    void Promise.all([
      pull('/master-data/ProductVariant'),
      pull('/master-data/ProductBundleItem'),
      pull('/master-data/Product'),
      pull('/master-data/ProductBundle'),
    ]).then(([v, bi, prods, b]) => {
      setVariants(v);
      setBundleItems(bi);
      setBundles(b);
      const map: Record<string, Record<string, unknown>> = {};
      for (const p of prods) map[String(p.id)] = p;
      setProductsById(map);
    });
  }, []);
  // Only bundles that actually have component items (hide orphaned/empty).
  const bundlesWithItems = useMemo(
    () => bundles.filter((b) => bundleItems.some((bi) => String(bi.productBundleId) === String(b.id ?? b.code))),
    [bundles, bundleItems],
  );

  const variantsFor = (productId: string | number | null) =>
    productId == null ? [] : variants.filter((v) => String(v.productId) === String(productId));

  function variantLabel(v: Record<string, unknown>): string {
    return [v.size, v.color].filter(Boolean).join(' / ') || String(v.name ?? v.sku ?? 'Variant');
  }

  /** #2 — expand a picked bundle into one line per component product. */
  function addBundle(bundle: Record<string, unknown> | null) {
    if (!bundle) return;
    const items = bundleItems.filter((bi) => String(bi.productBundleId) === String(bundle.id ?? bundle.code));
    if (!items.length) { toast.error('Empty bundle', 'This bundle has no products yet.'); return; }
    const bundleName = String(bundle.name ?? bundle.code ?? 'Bundle');
    const newLines: Line[] = items.map((bi) => {
      const prod = productsById[String(bi.productId)] ?? {};
      return {
        key: rid(),
        productId: (bi.productId ?? null) as number | null,
        productLabel: `${bundleName}: ${String(prod.name ?? bi.productId ?? '')}`,
        hsnCode: String(prod.hsnCode ?? ''),
        qty: Number(bi.quantity ?? 1) || 1,
        unit: String(prod.unitOfMeasure ?? ''),
        rate: Number(bi.unitPrice ?? prod.price ?? prod.mrp ?? 0),
        discountPct: 0,
        taxRate: Number(prod.taxRate ?? 18) || 18,
      };
    });
    setLines((ls) => {
      const real = ls.filter((l) => l.productId != null);
      return [
        ...real, ...newLines,
        { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 },
      ];
    });
    toast.success('Bundle added', `${items.length} item(s) from ${bundleName}`);
  }

  // Loading / saving
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<'draft' | 'post' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Quick-Add Customer inline modal (name + mobile only).
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddPhone, setQuickAddPhone] = useState('');
  const [quickAddBusy, setQuickAddBusy] = useState(false);

  // Hydrate when editing — pre-fill EVERYTHING the user saved.
  useEffect(() => {
    if (!isEdit || !rawId) return;
    setLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        // 1. Header — find the row by code or id from the list.
        const hr = await apiClient.get('/sales/SalesInvoice');
        const rows = (Array.isArray(hr.data) ? hr.data : (hr.data?.items ?? hr.data?.data ?? [])) as Array<Record<string, unknown>>;
        const d = rows.find(
          (x) => String(x.code ?? '') === String(rawId) || String(x.id ?? '') === String(rawId),
        );
        if (!d || cancelled) { if (!cancelled) setLoading(false); return; }

        setSavedCode(String(d.code ?? ''));
        setSavedHeaderTotal(Number(d.grandTotal ?? d.totalAmount ?? 0) || 0);
        setInvoiceNumber(String(d.invoiceNumber ?? d.code ?? ''));
        setInvoiceDate(String(d.invoiceDate ?? '').slice(0, 10) || invoiceDate);
        setDueDate(String(d.dueDate ?? '').slice(0, 10) || dueDate);
        setPaymentTerms(String(d.paymentTerms ?? 'Net 30'));
        setNotes(String(d.notes ?? ''));
        setTerms(String(d.terms ?? terms));
        // Restore the customer chip so the picker shows the saved party.
        if (d.customerId != null) {
          setCustomer({
            customerId: String(d.customerId),
            id: Number(d.customerId),
            name: String(d.customerName ?? ''),
            gstin: String(d.customerGstin ?? ''),
          } as Customer);
        }

        // 2. Lines — fetch from the SEPARATE SalesInvoiceLine table and
        //    filter by this invoice's numeric id.
        const invId = d.id;
        const lr = await apiClient.get('/sales/SalesInvoiceLine');
        const lineRows = (Array.isArray(lr.data) ? lr.data : (lr.data?.items ?? lr.data?.data ?? [])) as Array<Record<string, unknown>>;
        const mine = lineRows
          .filter((l) => String(l.salesInvoiceId) === String(invId))
          .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
        if (!cancelled && mine.length) {
          const qtyOf = (l: Record<string, unknown>) => Number(l.quantity ?? 1) || 1;
          const rateOf = (l: Record<string, unknown>) => Number(l.unitPrice ?? 0);
          setLines([
            ...mine.map((l) => {
              const qty = qtyOf(l);
              const rate = rateOf(l);
              const taxable = Number(l.taxableAmount ?? qty * rate);
              const discPct = qty * rate > 0
                ? round2((1 - taxable / (qty * rate)) * 100)
                : 0;
              return {
                key: rid(),
                productId: (l.productId as number) ?? null,
                productVariantId: (l.productVariantId as number) ?? null,
                productLabel: String(l.productName ?? l.name ?? ''),
                hsnCode: String(l.hsnCode ?? ''),
                qty,
                unit: String(l.unitOfMeasure ?? ''),
                rate,
                discountPct: Number.isFinite(discPct) ? Math.max(0, discPct) : 0,
                taxRate: Number(l.taxRate ?? 18),
              };
            }),
            // trailing blank line for adding more
            { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 },
          ]);
        } else if (!cancelled) {
          // No SalesInvoiceLine rows — POS / dine-in invoices keep their items
          // in the header `description` JSON. Recover them so Edit shows the
          // lines (and the total computes correctly) instead of "no lines".
          try {
            const env = JSON.parse(String(d.description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
            const src = Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []);
            if (src.length) {
              setLines([
                ...src.map((l) => {
                  const qty = Number(l.quantity ?? l.qty ?? 1) || 1;
                  const rate = Number(l.unitPrice ?? l.rate ?? l.price ?? 0);
                  return {
                    key: rid(),
                    productId: (l.productId as number) ?? null,
                    productVariantId: null,
                    productLabel: String(l.name ?? l.productName ?? l.itemName ?? ''),
                    hsnCode: String(l.hsnCode ?? ''),
                    qty,
                    unit: String(l.unit ?? l.unitOfMeasure ?? ''),
                    rate,
                    discountPct: 0,
                    taxRate: Number(l.taxRate ?? 18),
                  };
                }),
                { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 },
              ]);
            }
          } catch { /* no description envelope */ }
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawId]);

  // Place-of-supply logic — interstate vs intrastate.
  const customerState = useMemo(() => {
    const gstin = String(customer?.gstin ?? '').slice(0, 2);
    return gstin || (customer?.state ? '' : '');
  }, [customer]);

  const isInterstate = useMemo(() => {
    if (!customerState) return false; // unknown — treat as intrastate
    return customerState !== DEFAULT_PLACE_OF_SUPPLY;
  }, [customerState]);

  // Totals
  // ----- Coupon (applied to the whole bill) --------------------------------
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponRow | null>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  // ----- Gift card (redeemed as a tender against the bill) -----------------
  const [giftInput, setGiftInput] = useState('');
  const [appliedGift, setAppliedGift] = useState<GiftCardRow | null>(null);
  const [giftRedeem, setGiftRedeem] = useState(0);
  const [giftMsg, setGiftMsg] = useState<string | null>(null);
  const [giftBusy, setGiftBusy] = useState(false);

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    const lineTotals: { taxable: number; tax: number; total: number }[] = [];
    for (const l of lines) {
      const gross = (Number(l.qty) || 0) * (Number(l.rate) || 0);
      const discount = gross * ((Number(l.discountPct) || 0) / 100);
      const taxable = gross - discount;
      const tax = taxable * ((Number(l.taxRate) || 0) / 100);
      subtotal += gross;
      totalDiscount += discount;
      totalTax += tax;
      lineTotals.push({ taxable, tax, total: taxable + tax });
    }
    const taxable = subtotal - totalDiscount;
    // Bill-level coupon, clamped so it never exceeds the payable amount.
    const coupon = Math.min(Math.max(0, couponDiscount), Math.max(0, taxable + totalTax));
    // Gift card is a tender redeemed AFTER the coupon, clamped to what's left.
    const gift = Math.min(Math.max(0, giftRedeem), Math.max(0, taxable + totalTax - coupon));
    const grand = taxable + totalTax - coupon - gift;
    const rounded = Math.round(grand);
    const roundOff = Number((rounded - grand).toFixed(2));
    return {
      subtotal,
      totalDiscount,
      couponDiscount: coupon,
      giftCard: gift,
      taxable,
      totalTax,
      cgst: isInterstate ? 0 : totalTax / 2,
      sgst: isInterstate ? 0 : totalTax / 2,
      igst: isInterstate ? totalTax : 0,
      roundOff,
      grand: rounded,
      lineTotals,
    };
  }, [lines, isInterstate, couponDiscount, giftRedeem]);

  async function applyCouponNow() {
    const code = couponInput.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponMsg(null);
    try {
      const base = totals.subtotal - totals.totalDiscount; // pre-coupon taxable
      const coupon = await lookupCoupon(code);
      const res = applyCoupon(coupon, base);
      if (res.ok) {
        setAppliedCoupon(res.coupon ?? null);
        setCouponDiscount(res.discount);
        setCouponMsg(res.message);
      } else {
        setAppliedCoupon(null);
        setCouponDiscount(0);
        setCouponMsg(res.message);
      }
    } finally {
      setCouponBusy(false);
    }
  }
  function removeCoupon() {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponInput('');
    setCouponMsg(null);
  }

  async function applyGiftCardNow() {
    const code = giftInput.trim();
    if (!code) return;
    setGiftBusy(true);
    setGiftMsg(null);
    try {
      // Redeem against whatever is still payable after line discounts + coupon.
      const base = (totals.subtotal - totals.totalDiscount) + totals.totalTax - totals.couponDiscount;
      const card = await lookupGiftCard(code);
      const res = applyGiftCard(card, base);
      if (res.ok) {
        setAppliedGift(res.card ?? null);
        setGiftRedeem(res.redeem);
        setGiftMsg(res.message);
      } else {
        setAppliedGift(null);
        setGiftRedeem(0);
        setGiftMsg(res.message);
      }
    } finally {
      setGiftBusy(false);
    }
  }
  function removeGift() {
    setAppliedGift(null);
    setGiftRedeem(0);
    setGiftInput('');
    setGiftMsg(null);
  }

  function updateLine(idx: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [
      ...ls,
      { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 },
    ]);
  }
  function removeLine(idx: number) {
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, i) => i !== idx)));
  }
  function pickProduct(idx: number, p: Product | null) {
    if (!p) {
      updateLine(idx, { productId: null, productLabel: '', hsnCode: '', unit: '' });
      return;
    }
    // Resolve tax rate — prefer product's own taxRate (§35.2 new schema)
    // then fall back to its TaxClass.rate (legacy) then 18% default.
    const productRow = p as Record<string, unknown>;
    const directTaxRate = Number(productRow.taxRate ?? 0);
    const taxClass = taxClasses.find(
      (t) => String(t.taxClassId ?? t.id) === String(productRow.taxClassId ?? ''),
    );
    const taxRate = directTaxRate > 0
      ? directTaxRate
      : Number(taxClass?.rate ?? 18);
    // Read price + unit using new schema names, falling back to legacy.
    const rate = Number(
      productRow.price ?? productRow.sellingPrice ?? productRow.mrp ?? 0,
    );
    const unit = String(
      productRow.unitOfMeasure ?? productRow.unit ?? '',
    );
    const pickedId =
      (productRow.id as number | undefined)
      ?? (p.productId as string | number | null | undefined)
      ?? null;

    setLines((ls) => {
      // If this product already sits on another (real) line, just bump its
      // qty instead of adding a duplicate row — then drop the line the user
      // was editing (it would otherwise become an empty/leftover row).
      const existingIdx = ls.findIndex(
        (l, i) => i !== idx && l.productId != null && String(l.productId) === String(pickedId),
      );
      if (existingIdx >= 0) {
        let next = ls.map((l, i) =>
          i === existingIdx ? { ...l, qty: Number(l.qty || 0) + 1 } : l,
        );
        // Remove the row being edited (the one we picked into), unless it's
        // the only row — in that case reset it to blank.
        if (next.length > 1) {
          next = next.filter((_, i) => i !== idx);
        } else {
          next = next.map((l, i) =>
            i === idx ? { key: l.key, productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 } : l,
          );
        }
        // Keep a trailing blank line for the next pick.
        const last = next[next.length - 1];
        if (!last || last.productId != null) {
          next = [...next, { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 }];
        }
        return next;
      }

      // New product → fill the current line.
      let next = ls.map((l, i) =>
        i === idx
          ? {
              ...l,
              productId: pickedId,
              productLabel: String(p.name ?? p.code ?? ''),
              hsnCode: String(productRow.hsnCode ?? ''),
              unit,
              qty: 1,
              rate,
              taxRate,
            }
          : l,
      );
      // Auto-append a blank line so the user can keep picking.
      const last = next[next.length - 1];
      if (last && last.productId != null) {
        next = [...next, { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, unit: '', rate: 0, discountPct: 0, taxRate: 18 }];
      }
      return next;
    });
  }

  function validate(): string | null {
    if (!customer) return 'Pick a customer first.';
    // Only validate REAL lines — pickProduct auto-appends a blank trailing
    // line, and the save payload drops it, so it must not fail validation.
    const realLines = lines.filter((l) => l.productId != null || l.productLabel.trim() !== '');
    if (realLines.length === 0) return 'Add at least one product.';
    for (const [i, l] of realLines.entries()) {
      if (!l.productLabel) return `Line ${i + 1}: select a product.`;
      if (!Number.isFinite(Number(l.qty)) || Number(l.qty) <= 0) return `Line ${i + 1}: qty must be > 0.`;
      if (!Number.isFinite(Number(l.rate)) || Number(l.rate) < 0) return `Line ${i + 1}: rate must be ≥ 0.`;
    }
    return null;
  }

  /**
   * Inline "Quick add customer" — POSTs name + mobile only, gets the
   * created row back, sets it as the picked customer. The full CRM
   * customer form is still available at /panel/customers/new for
   * proper records (with GSTIN, billing address, etc.).
   */
  async function quickAddCustomer() {
    const name = quickAddName.trim();
    const phone = quickAddPhone.trim();
    if (!name) {
      toast.error('Name required');
      return;
    }
    if (phone && !/^\d{10}$/.test(phone)) {
      toast.error('Mobile must be 10 digits');
      return;
    }
    setQuickAddBusy(true);
    try {
      const code = 'CUS-' + Date.now().toString(36).toUpperCase().slice(-6);
      const r = await apiClient.post('/crm/Customer', {
        code,
        name,
        description: '',
        status: 0,
        remarks: '',
        customerType: 'B2C',
        phone: phone || '',
        email: '',
        gstin: '',
        pan: '',
        billingAddress: '',
        shippingAddress: '',
        city: '',
        state: '',
        pinCode: '',
        country: 'India',
        currency: 'INR',
        creditLimit: 0,
        outstandingBalance: 0,
        loyaltyPoints: 0,
      });
      const created = (r.data?.data ?? r.data) as Customer;
      setCustomer(created);
      setCustomerReloadKey((k) => k + 1); // make the picker re-fetch its list
      setQuickAddOpen(false);
      setQuickAddName('');
      setQuickAddPhone('');
      toast.success('Customer added', `${name} ready to bill`);
    } catch (e) {
      toast.error('Quick add failed', e instanceof Error ? e.message : 'Try the full New Customer form');
    } finally {
      setQuickAddBusy(false);
    }
  }

  /**
   * Build the InvoicePrintInput shape used by both Print and the
   * upcoming download/share flows. Computed inside render so all
   * the totals + line state are current.
   */
  async function buildPrintPayload(): Promise<InvoicePrintInput> {
    const realLines = lines.filter((l) => l.productId != null || l.productLabel.trim() !== '');
    // The in-memory customer chip may only carry name + GSTIN — fetch the
    // full record for address / phone / email so the print isn't sparse.
    let cust: Record<string, unknown> = { ...((customer as Record<string, unknown>) ?? {}) };
    const cid = customer?.customerId ?? customer?.id;
    if (cid != null) {
      try {
        const d = (await apiClient.get('/crm/Customer')).data;
        const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
        const full = rows.find((x) => String(x.id ?? x.customerId) === String(cid));
        if (full) cust = { ...full, ...cust };
      } catch { /* fall back to the chip */ }
    }
    const gstin = String(cust.gstin ?? customer?.gstin ?? '');
    const billTo = {
      label: 'Details Of Receiver (Billed To)',
      name: String(cust.name ?? customer?.name ?? '—'),
      address: String(cust.address ?? cust.billingAddress ?? ''),
      city: cust.city ? String(cust.city) : undefined,
      state: cust.state ? String(cust.state) : (customerState ? (STATE_CODE_MAP[customerState] ?? customerState) : undefined),
      pincode: cust.pincode ? String(cust.pincode) : undefined,
      gstin,
      phone: String(cust.phone ?? cust.mobile ?? customer?.phone ?? ''),
      email: String(cust.email ?? ''),
    };
    // Inlined branding: uploaded logo/signature/QR are auth-only image URLs
    // that a print window can't fetch — inline them as data URIs so they render.
    const b = await getPrintBrandingInlined();
    const tenant = await getTenantHeaderInlined();
    // Per-HSN tax summary — group lines by HSN; split CGST/SGST vs IGST by state.
    const hsnMap = new Map<string, HsnSummaryRow>();
    for (const l of realLines) {
      const lt = totals.lineTotals[lines.indexOf(l)] ?? { taxable: 0, tax: 0, total: 0 };
      const hsn = String(l.hsnCode ?? '') || '—';
      const rate = Number(l.taxRate) || 0;
      const cur = hsnMap.get(hsn) ?? { hsn, taxable: 0, cgstRate: 0, cgst: 0, sgstRate: 0, sgst: 0, igstRate: 0, igst: 0 };
      cur.taxable = round2(cur.taxable + lt.taxable);
      if (isInterstate) { cur.igstRate = rate; cur.igst = round2(cur.igst + lt.tax); }
      else { cur.cgstRate = rate / 2; cur.cgst = round2(cur.cgst + lt.tax / 2); cur.sgstRate = rate / 2; cur.sgst = round2(cur.sgst + lt.tax / 2); }
      hsnMap.set(hsn, cur);
    }
    return {
      docType: 'TAX INVOICE',
      number: invoiceNumber || savedCode || 'DRAFT',
      date: invoiceDate,
      dueDate,
      customerType: gstin ? 'B2B' : 'B2C',
      tenant,
      placeOfSupply: customerState ? (STATE_CODE_MAP[customerState] ?? customerState) : undefined,
      panNumber: gstin ? gstin.slice(2, 12) : (cust.pan ? String(cust.pan) : undefined),
      paymentTerms,
      parties: [billTo, { ...billTo, label: 'Details Of Consignee (Shipped To)' }],
      lines: realLines.map((l) => {
        const lt = totals.lineTotals[lines.indexOf(l)] ?? { taxable: 0, tax: 0, total: 0 };
        return {
          description: l.productLabel,
          hsnCode: l.hsnCode,
          qty: Number(l.qty),
          unit: l.unit,
          rate: Number(l.rate),
          discountPct: Number(l.discountPct),
          tradeDiscount: round2(Number(l.rate) * Number(l.qty) * Number(l.discountPct) / 100),
          taxRate: Number(l.taxRate),
          taxable: round2(lt.taxable),
          total: round2(lt.total),
        };
      }),
      subTotal: round2(totals.subtotal),
      totalDiscount: round2(totals.totalDiscount),
      taxableAmount: round2(totals.taxable),
      cgstAmount: round2(totals.cgst),
      sgstAmount: round2(totals.sgst),
      igstAmount: round2(totals.igst),
      roundOff: totals.roundOff,
      totalAmount: totals.grand,
      notes,
      terms,
      freightCharges: 0,
      hsnSummary: [...hsnMap.values()],
      bank: (b.bankName || b.bankAccountNo) ? { name: b.bankName, accountName: b.bankAccountName, accountNo: b.bankAccountNo, ifsc: b.bankIfsc, branch: b.bankBranch } : undefined,
      signatureUrl: b.signatureUrl,
      qrUrl: printQrUrl(b.qrUrl, ['TAX INVOICE', invoiceNumber || savedCode || '', billTo.name, 'Amt:' + totals.grand, tenant.gstin]),
      couponDiscount: round2(totals.couponDiscount),
      couponCode: appliedCoupon ? (appliedCoupon.couponCode ?? appliedCoupon.code) : undefined,
    };
  }

  async function save(status: 'Draft' | 'Posted') {
    const e = validate();
    if (e) {
      setErr(e);
      toast.error('Fix errors', e);
      return;
    }
    setSaving(status === 'Draft' ? 'draft' : 'post');
    setErr(null);
    try {
      // §35 live SalesInvoice columns (verified via probe): subTotal,
      // discountAmount, taxAmount, roundingAmount, grandTotal, paidAmount,
      // balance, paymentStatus, invoiceStatus. The KPIs + grid read
      // grandTotal + balance — so we MUST write those exact names or the
      // list shows ₹0 totals/outstanding.
      const grand = totals.grand;
      // Clamp the collected amount to [0, grand]. Only a Posted invoice records
      // a payment (a draft hasn't been issued yet).
      const paidNow = status === 'Posted' ? Math.min(Math.max(0, Number(amountReceived) || 0), grand) : 0;
      // A NON-EMPTY, unique invoice number is required: the create POST returns
      // no id, so we recover it by filtering on invoiceNumber afterwards. A
      // blank number would make recovery refuse → lines dropped. Fall back to a
      // unique stamp if the field was somehow left empty.
      const ensuredNumber = invoiceNumber || `SI-${Date.now().toString(36).toUpperCase().slice(-8)}`;
      const payload = {
        invoiceNumber: ensuredNumber,
        invoiceDate,
        dueDate,
        customerId: customer!.customerId ?? customer!.id,
        customerName: customer!.name,
        customerGstin: customer!.gstin,
        currency: 'INR',
        placeOfSupply: customerState || DEFAULT_PLACE_OF_SUPPLY,
        paymentTerms,
        // SalesInvoice has no couponCode / giftCard column → stash the applied
        // coupon + redeemed gift card in notes so they're recorded with it.
        notes: [
          notes,
          appliedCoupon ? `Coupon: ${appliedCoupon.couponCode ?? appliedCoupon.code} (−₹${round2(totals.couponDiscount)})` : '',
          appliedGift ? `Gift card: ${appliedGift.cardNumber ?? appliedGift.code} (−₹${round2(totals.giftCard)})` : '',
        ].filter(Boolean).join(' | ') || undefined,
        terms: terms || undefined,
        invoiceStatus: status,                       // Draft | Posted
        invoiceType: 'Self',                         // source channel (Self/POS/Dine-in)
        paymentStatus: paymentStatusFor(paidNow, grand),
        subTotal: round2(totals.subtotal),
        // Bill discount = line discounts + bill-level coupon + gift-card redeem.
        discountAmount: round2(totals.totalDiscount + totals.couponDiscount + totals.giftCard),
        taxAmount: round2(totals.totalTax),
        roundingAmount: totals.roundOff,
        grandTotal: grand,                           // ← list "Total"
        paidAmount: round2(paidNow),
        balance: round2(grand - paidNow),            // ← list "Outstanding"
        // keep legacy aliases too, harmless if ignored:
        totalAmount: grand,
        taxableAmount: round2(totals.taxable),
        // Recoverable fallback: a COMPACT, length-capped copy of the lines in
        // the header `description`. Must stay under the column's (small,
        // undocumented) limit or the whole POST 500s with "String or binary
        // data would be truncated". The SalesInvoiceLine table holds the full
        // lines; this is only for re-hydrating Edit if that POST failed.
        description: compactDescEnvelope(
          lines
            .filter((l) => l.productId != null || l.productLabel.trim() !== '')
            .map((l) => ({ name: l.productLabel, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate, productId: l.productId })),
        ),
        status: 0,                                   // row-level (0=Active)
      };

      // Lines live in the SEPARATE `/sales/SalesInvoiceLine` table keyed by
      // `salesInvoiceId` — they are NOT a nested array on the header (the
      // backend ignores a nested `lines`). So: save the header, capture its
      // numeric id, then POST each line individually with that FK.
      const realLines = lines.filter((l) => l.productId != null || l.productLabel.trim() !== '');

      // The deployed backend validates the header's SubTotal against the
      // invoice's LINE ITEMS ("Invoice does not balance: items=N header=M").
      // At create time there are no lines yet, so create the header with ZERO
      // monetary totals (0 balances 0), post the lines, then reconcile the real
      // totals with a follow-up PUT once the lines exist.
      const ZERO_TOTALS = {
        subTotal: 0, discountAmount: 0, taxAmount: 0, roundingAmount: 0,
        grandTotal: 0, paidAmount: 0, balance: 0, totalAmount: 0, taxableAmount: 0,
        paymentStatus: 'Unpaid',
      };

      let invoiceId: number | string | undefined;
      let invoiceCode: string | undefined;
      if (isEdit && rawId) {
        // The list links by id, so rawId is numeric; the real-totals PUT below
        // (after lines are re-posted) carries the updated header fields.
        const numId = Number(rawId);
        invoiceId = Number.isFinite(numId) && numId > 0 ? numId : rawId;
        invoiceCode = savedCode ?? rawId;
      } else {
        // POST /sales/SalesInvoice returns 200 with an EMPTY body (no id), so
        // recover the id by the unique invoiceNumber — otherwise the line-post
        // block below is skipped and the invoice saves with ZERO line rows.
        const { id, code } = await createHeaderAndGetId('/sales/SalesInvoice', { ...payload, ...ZERO_TOTALS }, {
          matchField: 'invoiceNumber',
          matchValue: payload.invoiceNumber as string,
        });
        invoiceId = id;
        invoiceCode = code ?? (payload.invoiceNumber as string) ?? undefined;
      }

      // On edit, clear the old lines first so qty/price edits don't pile up.
      if (isEdit && invoiceId != null) {
        try {
          const ex = await apiClient.get('/sales/SalesInvoiceLine');
          const exRows = (Array.isArray(ex.data) ? ex.data : (ex.data?.items ?? ex.data?.data ?? [])) as Array<Record<string, unknown>>;
          await Promise.all(
            exRows
              .filter((row) => String(row.salesInvoiceId) === String(invoiceId))
              .map((row) => apiClient.delete(`/sales/SalesInvoiceLine/${row.id}`).catch(() => undefined)),
          );
        } catch { /* best-effort cleanup */ }
      }

      // POST each line against the line table — SEQUENTIALLY so a parallel
      // burst can't race the auth refresh and silently drop lines (which left
      // invoices showing only some of their products).
      if (invoiceId != null && realLines.length) {
        const payloads = realLines.map((l, i) => {
          const lt = totals.lineTotals[lines.indexOf(l)] ?? { taxable: 0, tax: 0, total: 0 };
          return {
            // Line `code` is UNIQUE per tenant on the backend. The old
            // `${invoiceCode ?? 'SI'}-L${n}` produced "-L1" for every invoice
            // (code is blank) → 409 duplicate_code dropped all but the first
            // invoice's lines. Key off the invoice id (always unique) so codes
            // never collide across invoices.
            code: `${invoiceId}-L${i + 1}`,
            name: l.productLabel || `Line ${i + 1}`,
            status: 0,
            salesInvoiceId: invoiceId,
            lineNumber: i + 1,
            productId: l.productId,
            productVariantId: l.productVariantId ?? null,
            productCode: '',
            productName: l.productLabel,
            sku: '',
            quantity: Number(l.qty),
            unitOfMeasure: l.unit || 'PCS',
            unitPrice: Number(l.rate),
            lineDiscount: round2(Number(l.rate) * Number(l.qty) * Number(l.discountPct) / 100),
            taxableAmount: round2(lt.taxable),
            taxRate: Number(l.taxRate),
            taxAmount: round2(lt.tax),
            lineTotal: round2(lt.total),
          };
        });
        const savedCount = await postLinesSequential('/sales/SalesInvoiceLine', payloads);
        if (savedCount < payloads.length) {
          toast.error('Some items not saved', `${savedCount}/${payloads.length} lines stored — re-open & save again to retry the rest.`);
        }
      }

      // Reconcile the REAL header totals now that the line items exist, so the
      // backend's balance check passes (header SubTotal == sum of items) and the
      // grid/reports show the right amounts. Best-effort: if it fails, the
      // invoice + lines are already saved.
      if (invoiceId != null) {
        const numId = Number(invoiceId);
        const keyFields = Number.isFinite(numId) && numId > 0
          ? { id: numId, ...(invoiceCode && invoiceCode !== String(numId) ? { code: invoiceCode } : {}) }
          : { code: String(invoiceId) };
        try {
          await apiClient.put('/sales/SalesInvoice', { ...payload, ...keyFields });
        } catch {
          toast.error('Totals not updated', 'Invoice and items were saved; the header totals will reconcile on the server.');
        }
      }

      // Sales ↔ Inventory: a posted invoice issues goods → stock OUT.
      if (status === 'Posted' && invoiceId != null && realLines.length) {
        const warehouseId = await resolveDefaultWarehouseId();
        await postStockMovements({
          direction: 'Out',
          sourceType: 'SalesInvoice',
          sourceId: invoiceId,
          docNumber: String(invoiceCode ?? invoiceNumber ?? invoiceId),
          occurredAt: invoiceDate ? new Date(invoiceDate).toISOString() : new Date().toISOString(),
          warehouseId,
          lines: realLines.map((l) => ({ productId: l.productId, quantity: Number(l.qty) || 0, unitCost: Number(l.rate) || 0, unitOfMeasure: l.unit })),
        });
      }

      // Payments ↔ Billing: record the amount collected as a SalesPayment.
      if (paidNow > 0 && invoiceId != null) {
        await postSalesPayment({
          salesInvoiceId: invoiceId,
          amount: paidNow,
          method: payMethod,
          docNumber: String(invoiceCode ?? invoiceNumber ?? invoiceId),
        });
      }

      toast.success('Saved', `Invoice ${status === 'Posted' ? 'posted' : (isEdit ? 'updated' : 'saved as draft')}`);
      navigate('/panel/sales/invoices');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(null);
    }
  }

  // --- Print / Share / WhatsApp actions (consolidated into one dropdown) ---
  const [actionsOpen, setActionsOpen] = useState(false);
  const realLineCount = lines.filter((l) => l.productId != null || l.productLabel.trim() !== '').length;
  const canShare = !!customer && realLineCount > 0 && !saving && !loading;

  async function doPrint() {
    printInvoiceByFormat(getAssignedFormat('salesInvoice'), await buildPrintPayload());
  }
  function doWhatsApp() {
    const phone = String(customer?.phone ?? '').replace(/\D/g, '').replace(/^0+/, '');
    const intl = phone.length === 10 ? `91${phone}` : phone;
    const msg = encodeURIComponent(
      `Hi ${customer?.name ?? 'customer'},\n\n` +
      `Your invoice ${invoiceNumber || 'DRAFT'} dated ${invoiceDate}\n` +
      `Total: ₹ ${inr(totals.grand)}\n` +
      `Items: ${realLineCount}\n\n` +
      `Thank you!`,
    );
    window.open(`https://wa.me/${intl}?text=${msg}`, '_blank', 'noopener');
  }
  async function doShare() {
    const text =
      `Invoice ${invoiceNumber || 'DRAFT'} dated ${invoiceDate}\n` +
      `Bill to: ${customer?.name ?? '—'}\n` +
      `Total: ₹ ${inr(totals.grand)} · ${realLineCount} item(s)`;
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (typeof nav.share === 'function') {
        await nav.share({ title: `Invoice ${invoiceNumber || ''}`.trim(), text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Copied', 'Invoice summary copied to clipboard');
      }
    } catch { /* user dismissed the share sheet — no-op */ }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/sales/invoices')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit invoice' : 'New sales invoice'}
          </h1>
          <p className="text-xs text-content-muted">
            {customer
              ? `Bill to ${customer.name ?? '—'}${
                  customer.gstin ? ` · GSTIN ${customer.gstin}` : ''
                }${customerState ? ` · ${STATE_CODE_MAP[customerState] ?? customerState}` : ''}${
                  customer.gstin ? (isInterstate ? ' · IGST' : ' · CGST + SGST') : ''
                }`
              : 'Pick a customer to start.'}
          </p>
        </div>
        <Button variant="outline" disabled={!!saving || loading} onClick={() => save('Draft')}>
          <Save className="h-4 w-4" /> {saving === 'draft' ? 'Saving…' : 'Save draft'}
        </Button>
        {/* Print / Share / WhatsApp — consolidated dropdown button. */}
        <div className="relative">
          <Button
            variant="outline"
            disabled={!canShare}
            onClick={() => setActionsOpen((v) => !v)}
            title="Print, share, or send the invoice on WhatsApp"
          >
            <Share2 className="h-4 w-4" /> Print / Share
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${actionsOpen ? 'rotate-180' : ''}`} />
          </Button>
          {actionsOpen && (
            <>
              {/* click-away backdrop */}
              <div className="fixed inset-0 z-10" onClick={() => setActionsOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-border bg-surface-card py-1 shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted"
                  onClick={() => { setActionsOpen(false); doPrint(); }}
                >
                  <Printer className="h-4 w-4 text-content-muted" /> Print / Save PDF
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted"
                  onClick={() => { setActionsOpen(false); void doShare(); }}
                >
                  <Share2 className="h-4 w-4 text-content-muted" /> Share
                </button>
                <button
                  type="button"
                  disabled={!customer?.phone}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => { setActionsOpen(false); doWhatsApp(); }}
                  title={customer?.phone ? 'Open WhatsApp with the invoice summary' : 'Customer has no phone number'}
                >
                  <MessageCircle className="h-4 w-4 text-content-muted" /> WhatsApp
                </button>
              </div>
            </>
          )}
        </div>
        <Button disabled={!!saving || loading} onClick={() => save('Posted')}>
          <Send className="h-4 w-4" /> {saving === 'post' ? 'Posting…' : 'Save & post'}
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* ① Invoice has a saved total but no itemised line rows (e.g. older
          POS/restaurant invoices saved before line-persistence) — surface
          the header total so the screen doesn't read ₹0. */}
      {isEdit && realLineCount === 0 && savedHeaderTotal > 0 && (
        <Alert variant="warning">
          This invoice was saved with a total of ₹{inr(savedHeaderTotal)} but has no itemised
          product lines on record. The total above reflects the saved header; add lines below to
          itemise it.
        </Alert>
      )}

      <Card>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Invoice number</span>
            <input
              className={inputCls}
              value={invoiceNumber}
              onChange={(e) => { numberTouched.current = true; setInvoiceNumber(e.target.value); }}
              placeholder="auto-generated if blank"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Invoice date</span>
            <input
              type="date"
              className={inputCls}
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Due date</span>
            <input
              type="date"
              className={inputCls}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>
          <div className="flex flex-col gap-1 md:col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-content-secondary">Customer</span>
              <button
                type="button"
                onClick={() => setQuickAddOpen(true)}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                + Quick add (name + mobile)
              </button>
            </div>
            <EntityPicker<Customer>
              basePath="/crm/Customer"
              idKey="customerId"
              labelKey="name"
              secondaryKey="gstin"
              value={(customer?.customerId ?? customer?.id) as string | number | null}
              selectedRow={customer}
              reloadKey={customerReloadKey}
              onChange={setCustomer}
              placeholder="Search customer by name, code or GSTIN"
            />
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Payment terms</span>
            <input
              className={inputCls}
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Net 30, COD, Advance…"
            />
          </label>
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-content-primary">Line items</h3>
          <div className="flex items-center gap-2">
            {/* #2 — pick a bundle to append its component products as lines. */}
            <div className="w-52">
              <EntityPicker<Record<string, unknown>>
                basePath="/master-data/ProductBundle"
                idKey="id"
                labelKey="name"
                secondaryKey="code"
                rows={bundlesWithItems}
                value={null}
                onChange={(b) => addBundle(b)}
                placeholder="+ Add bundle"
              />
            </div>
            <Button size="sm" variant="outline" onClick={addLine}>
              <Plus className="h-4 w-4" /> Add line
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-content-muted">
              <tr className="border-b border-border">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Product</th>
                <th className="px-2 py-2 text-left">HSN</th>
                <th className="px-2 py-2 text-right">Qty</th>
                <th className="px-2 py-2 text-left">Unit</th>
                <th className="px-2 py-2 text-right">Rate (₹)</th>
                <th className="px-2 py-2 text-right">Disc %</th>
                <th className="px-2 py-2 text-right">GST %</th>
                <th className="px-2 py-2 text-right">Taxable</th>
                <th className="px-2 py-2 text-right">Tax</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const t = totals.lineTotals[i] ?? { taxable: 0, tax: 0, total: 0 };
                return (
                  <tr key={l.key} className="border-b border-border/40">
                    <td className="px-2 py-2 text-content-muted">{i + 1}</td>
                    <td className="px-2 py-2" style={{ minWidth: 220 }}>
                      <EntityPicker<Product>
                        basePath="/master-data/Product"
                        idKey="productId"
                        labelKey="name"
                        secondaryKey="code"
                        value={l.productId}
                        onChange={(p) => pickProduct(i, p)}
                        placeholder="Search product"
                        // On edit the product list isn't fetched until the field
                        // is focused; pass the hydrated id+label so the saved
                        // product shows immediately instead of a blank field.
                        selectedRow={l.productId != null
                          ? ({ productId: l.productId, name: l.productLabel } as Product)
                          : null}
                      />
                      {/* #1 — variant picker: only when the chosen product
                          has variants. Selecting one overrides the line's
                          rate + appends the variant to the label. */}
                      {variantsFor(l.productId).length > 0 && (
                        <select
                          className="mt-1 h-7 w-full rounded border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
                          value={String(l.productVariantId ?? '')}
                          onChange={(e) => {
                            const v = variants.find((x) => String(x.id) === e.target.value);
                            if (!v) { updateLine(i, { productVariantId: null }); return; }
                            const base = String(productsById[String(l.productId)]?.name ?? l.productLabel.split(' — ')[0]);
                            updateLine(i, {
                              productVariantId: (v.id ?? null) as number | null,
                              rate: Number(v.price ?? l.rate),
                              productLabel: `${base} — ${variantLabel(v)}`,
                            });
                          }}
                        >
                          <option value="">Base (no variant)</option>
                          {variantsFor(l.productId).map((v) => (
                            <option key={String(v.id)} value={String(v.id)}>
                              {variantLabel(v)}{v.price != null ? ` · ₹${v.price}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={inputCls}
                        value={l.hsnCode}
                        onChange={(e) => updateLine(i, { hsnCode: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                        placeholder="HSN/SAC"
                        style={{ width: 90 }}
                      />
                    </td>
                    <td className="px-2 py-2">
                      {/* Qty — adjust only via +/- steppers (no free typing). */}
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          className="grid h-7 w-7 place-items-center rounded-md border border-border text-content-secondary hover:border-primary disabled:opacity-40"
                          onClick={() => updateLine(i, { qty: Math.max(1, Number(l.qty || 1) - 1) })}
                          disabled={l.productId == null}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-8 text-center text-sm tabular-nums">{l.qty}</span>
                        <button
                          type="button"
                          className="grid h-7 w-7 place-items-center rounded-md border border-border text-content-secondary hover:border-primary disabled:opacity-40"
                          onClick={() => updateLine(i, { qty: Number(l.qty || 0) + 1 })}
                          disabled={l.productId == null}
                          aria-label="Increase quantity"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      {/* Unit — read-only; comes from the product master. */}
                      <input
                        className={`${inputCls} cursor-not-allowed bg-surface-muted text-content-muted`}
                        value={l.unit}
                        readOnly
                        placeholder="PCS"
                        style={{ width: 70 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      {/* Rate — read-only; comes from the product master. */}
                      <input
                        type="number"
                        className={`${inputCls} cursor-not-allowed bg-surface-muted text-right text-content-muted`}
                        value={l.rate}
                        readOnly
                        style={{ width: 100 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.discountPct}
                        min={0}
                        max={100}
                        step={0.01}
                        onChange={(e) => updateLine(i, { discountPct: Number(e.target.value) })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <select
                        className={`${inputCls} text-right`}
                        value={l.taxRate}
                        onChange={(e) => updateLine(i, { taxRate: Number(e.target.value) })}
                        style={{ width: 80 }}
                      >
                        {[0, 0.25, 3, 5, 12, 18, 28].map((r) => (
                          <option key={r} value={r}>{r}%</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.taxable)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.tax)}</td>
                    <td className="px-2 py-2 text-right font-medium tabular-nums">{inr(t.total)}</td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="text-content-muted hover:text-danger"
                        onClick={() => removeLine(i)}
                        aria-label="Remove line"
                        disabled={lines.length === 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Notes (visible to customer)</span>
              <textarea className={textareaCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Terms &amp; conditions</span>
              <textarea className={textareaCls} value={terms} onChange={(e) => setTerms(e.target.value)} />
            </label>
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 text-sm font-semibold text-content-primary">Totals</h3>
          <Row k="Subtotal" v={inr(totals.subtotal)} />
          <Row k="Discount" v={`− ${inr(totals.totalDiscount)}`} />
          <Row k="Taxable amount" v={inr(totals.taxable)} bold />
          {isInterstate ? (
            <Row k="IGST" v={inr(totals.igst)} />
          ) : (
            <>
              <Row k="CGST" v={inr(totals.cgst)} />
              <Row k="SGST" v={inr(totals.sgst)} />
            </>
          )}
          {totals.couponDiscount > 0 && (
            <Row k={`Coupon (${appliedCoupon?.couponCode ?? appliedCoupon?.code ?? ''})`} v={`− ${inr(totals.couponDiscount)}`} />
          )}
          {totals.giftCard > 0 && (
            <Row k={`Gift card (${appliedGift?.cardNumber ?? appliedGift?.code ?? ''})`} v={`− ${inr(totals.giftCard)}`} />
          )}
          <Row k="Round off" v={inr(totals.roundOff)} />
          {/* Apply-coupon control. */}
          <div className="mt-2 border-t border-border pt-2">
            {appliedCoupon ? (
              <div className="flex items-center justify-between gap-2 rounded border border-success/40 bg-success/10 px-2 py-1 text-xs text-success">
                <span>Coupon {appliedCoupon.couponCode ?? appliedCoupon.code} applied</span>
                <button type="button" className="text-content-muted hover:text-danger" onClick={removeCoupon} title="Remove coupon">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <input
                  className="h-8 flex-1 rounded border border-border bg-surface-card px-2 text-sm uppercase outline-none focus:border-primary"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder="Coupon code"
                  onKeyDown={(e) => e.key === 'Enter' && void applyCouponNow()}
                />
                <Button size="sm" variant="outline" onClick={() => void applyCouponNow()} disabled={couponBusy || !couponInput.trim()}>
                  {couponBusy ? '…' : 'Apply'}
                </Button>
              </div>
            )}
            {couponMsg && !appliedCoupon && <div className="mt-1 text-[11px] text-danger">{couponMsg}</div>}
          </div>
          {/* Redeem-gift-card control (acts as a tender against the bill). */}
          <div className="mt-2 border-t border-border pt-2">
            {appliedGift ? (
              <div className="flex items-center justify-between gap-2 rounded border border-success/40 bg-success/10 px-2 py-1 text-xs text-success">
                <span>Gift card {appliedGift.cardNumber ?? appliedGift.code} − {inr(totals.giftCard)}</span>
                <button type="button" className="text-content-muted hover:text-danger" onClick={removeGift} title="Remove gift card">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <input
                  className="h-8 flex-1 rounded border border-border bg-surface-card px-2 text-sm outline-none focus:border-primary"
                  value={giftInput}
                  onChange={(e) => setGiftInput(e.target.value)}
                  placeholder="Gift card number"
                  onKeyDown={(e) => e.key === 'Enter' && void applyGiftCardNow()}
                />
                <Button size="sm" variant="outline" onClick={() => void applyGiftCardNow()} disabled={giftBusy || !giftInput.trim()}>
                  {giftBusy ? '…' : 'Redeem'}
                </Button>
              </div>
            )}
            {giftMsg && !appliedGift && <div className="mt-1 text-[11px] text-danger">{giftMsg}</div>}
          </div>
          <div className="mt-2 border-t border-border pt-2">
            <Row k="Grand total" v={`₹ ${inr(realLineCount === 0 && savedHeaderTotal > 0 ? savedHeaderTotal : totals.grand)}`} big />
          </div>
          {/* Payment captured at invoice time → records a SalesPayment on Post. */}
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
            <span className="text-xs font-medium text-content-secondary">Amount received</span>
            <div className="flex items-center gap-1">
              <select
                className="h-8 rounded border border-border bg-surface-card px-1 text-xs outline-none focus:border-primary"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                title="Payment method"
              >
                {['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input
                type="number"
                className="h-8 w-28 rounded border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                value={amountReceived || ''}
                placeholder="0"
                onChange={(e) => setAmountReceived(Number(e.target.value) || 0)}
              />
              <button
                type="button"
                className="rounded border border-border px-2 text-[11px] text-primary hover:bg-surface-card-hover"
                onClick={() => setAmountReceived(totals.grand)}
                title="Mark fully paid"
              >
                Full
              </button>
            </div>
          </div>
          {amountReceived > 0 && (
            <Row k="Balance" v={`₹ ${inr(Math.max(0, totals.grand - amountReceived))}`} bold />
          )}
        </Card>
      </div>

      {/* Quick-Add Customer modal */}
      {quickAddOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !quickAddBusy && setQuickAddOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-surface-card p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content-primary">Quick add customer</h3>
              <button
                type="button"
                onClick={() => setQuickAddOpen(false)}
                disabled={quickAddBusy}
                className="rounded p-1 text-content-muted hover:bg-surface-card-hover"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-content-secondary">Name *</span>
                <input
                  className={inputCls}
                  value={quickAddName}
                  onChange={(e) => setQuickAddName(e.target.value)}
                  placeholder="Customer name"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && void quickAddCustomer()}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-content-secondary">Mobile number</span>
                <div className="flex h-9 items-center gap-1 rounded-md border border-border bg-surface-card pl-3">
                  <span className="text-sm text-content-muted">+91</span>
                  <input
                    className="h-full flex-1 bg-transparent px-2 text-sm outline-none"
                    value={quickAddPhone}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    onChange={(e) => setQuickAddPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onKeyDown={(e) => e.key === 'Enter' && void quickAddCustomer()}
                  />
                </div>
              </label>
              <p className="text-[11px] text-content-muted">
                Need GSTIN, billing address, etc.? Use the full New Customer form.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setQuickAddOpen(false)} disabled={quickAddBusy}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => void quickAddCustomer()} disabled={quickAddBusy || !quickAddName.trim()}>
                  {quickAddBusy ? 'Adding…' : 'Add + select'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, bold, big }: { k: string; v: string; bold?: boolean; big?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className={`text-xs ${big ? 'text-sm text-content-primary' : 'text-content-muted'}`}>{k}</span>
      <span
        className={`tabular-nums ${
          big ? 'text-base font-semibold text-content-primary' : bold ? 'font-medium' : 'text-content-primary'
        }`}
      >
        {v}
      </span>
    </div>
  );
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function rid(): string {
  return Math.random().toString(36).slice(2, 9);
}
